import {
    ChangeEvent,
    useEffect,
    useState,
    ReactNode,
    FocusEvent,
    KeyboardEvent,
    InputHTMLAttributes,
    useRef,
    Ref,
    MouseEvent,
    useImperativeHandle,
} from 'react';
import { useTheme } from 'styled-components';
import { InformationType, Size } from '../../types';
import type { IconComponent } from '../../themes/base/icons/types';
import { Icon } from '../icon';
import {
    Wrapper,
    Container,
    Input,
    Label,
    SlimLabel,
    Content,
    Unit,
    CurrencySymbol,
    ReadOnlyIcon,
    HelpIcon,
    IconWrapper,
    ICON_SIZE,
} from './text-input.styles';
import { InputMessage } from '../input-message';
import { Currency, CurrencyPosition } from '../../utils/types';
import { getCurrencyPositionFromLocation, getCurrencySymbol } from '../../utils/currency';
import { WithTooltip } from '../tooltip';
import { Composite } from '../../structure';
import { snapTimeToStep, stepTimeSegment } from './text-input.helper';
import { InformationIcon, LockCircleIcon } from '../../themes/base/icons/named';
/**
 * The input's own attributes pass through (`autoComplete`, `enterKeyHint`, `inputMode`, `aria-*`,
 * ...), except those TextInput sets or means something else by.
 */
type NativeInputProps = Omit<
    InputHTMLAttributes<HTMLInputElement>,
    | 'value'
    | 'onChange'
    | 'onBlur'
    | 'onFocus'
    | 'onKeyDown'
    | 'size'
    | 'type'
    | 'max'
    | 'min'
    | 'step'
>;

export interface TextInputProps extends NativeInputProps {
    label?: string;
    mode?: TextInputMode;
    name: string;
    id?: string;
    disabled?: boolean;
    value?: string;
    onChange?: (text: string) => void;
    onBlur?: (text: string) => void;
    message?: string | ReactNode;
    /**
     * Marks the field invalid without requiring a message.
     *
     * <p>For a form that renders its own validation text — so it can animate it, or place it
     * somewhere the field cannot. Passing a message with {@link InformationType.ERROR} still works
     * and still styles the field; this is the half of it that does not change the layout.
     */
    invalid?: boolean;
    icon?: IconComponent;
    slim?: boolean;
    size?: Size;
    informationType?: InformationType;
    unit?: string;
    locale?: string;
    currency?: Currency;
    ref?: Ref<HTMLInputElement>;
    /** @deprecated Use autoComplete, the input's own attribute. */
    autocomplete?: string;
    flex?: string;
    readOnly?: boolean;
    placeholder?: string;
    max?: number;
    min?: number;
    step?: number;
    helpText?: string | ReactNode;
}
export enum TextInputMode {
    PASSWORD = 'password',
    EMAIL = 'email',
    PLAIN_TEXT = 'text',
    NUMBER = 'number',
    CURRENCY = 'currency',
    PHONE = 'phone',
    TIME = 'time',
}
const modeToType = {
    [TextInputMode.PASSWORD]: 'password',
    [TextInputMode.EMAIL]: 'email',
    [TextInputMode.PLAIN_TEXT]: 'text',
    [TextInputMode.NUMBER]: 'number',
    [TextInputMode.CURRENCY]: 'text',
    [TextInputMode.PHONE]: 'tel',
    [TextInputMode.TIME]: 'time',
};
export function TextInput(props: TextInputProps) {
    const {
        label,
        name,
        id,
        disabled,
        value: initialValue,
        mode = TextInputMode.PLAIN_TEXT,
        onChange = () => {},
        onBlur = () => {},
        message,
        invalid = false,
        icon,
        slim,
        size = Size.NORMAL,
        informationType = InformationType.INFO,
        unit,
        locale = 'en',
        currency = Currency.EUR,
        autocomplete,
        flex,
        readOnly = false,
        placeholder,
        max,
        min,
        step,
        helpText,
        autoComplete,
        ...nativeProps
    } = props;
    const internalRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(props.ref, () => internalRef.current!);

    const [currencyPosition, setCurrencyPosition] = useState<CurrencyPosition>(
        CurrencyPosition.PREFIX
    );
    const [currencySymbol, setCurrencySymbol] = useState<string>('');
    const [value, setValue] = useState<string>(initialValue ?? '');
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const theme = useTheme();
    useEffect(() => {
        setValue(initialValue ?? '');
    }, [initialValue]);
    useEffect(() => {
        if (mode === TextInputMode.CURRENCY) {
            setCurrencyPosition(getCurrencyPositionFromLocation(locale));
        }
    }, [mode, locale]);
    useEffect(() => {
        if (mode === TextInputMode.CURRENCY && currency) {
            setCurrencySymbol(getCurrencySymbol(locale, currency));
        }
    }, [mode, currency, locale]);
    const onTextChange = (event: ChangeEvent<HTMLInputElement>) => {
        setValue(event.target.value);
        onChange(event.target.value);
    };
    const onTextBlur = (event: FocusEvent<HTMLInputElement>) => {
        setIsFocused(false);
        let next = event.target.value;
        if (mode === TextInputMode.TIME && step && step !== 60 && next) {
            const snapped = snapTimeToStep(next, step);
            if (snapped !== next) {
                next = snapped;
                setValue(next);
                onChange(next);
                // Force the native input to reflect the snapped value. React's
                // controlled-value reconciliation for <input type="time"> can
                // skip the DOM write in some browsers (notably Safari), leaving
                // the user's pre-snap value visible even though state updated.
                if (internalRef.current) {
                    internalRef.current.value = next;
                }
            }
        }
        onBlur(next);
    };
    const onTextFocus = () => {
        setIsFocused(true);
    };
    const onTimeKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (mode !== TextInputMode.TIME || !step || step === 60) return;
        if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
        // The browser exposes no caret/segment API for <input type="time">, so we
        // can't know up front whether the hour or minute segment is focused. Let
        // the native ±1 move happen first, then diff before/after: if the minute
        // segment changed we upgrade it to a full step interval; if the hour
        // segment changed we leave the native result untouched.
        const direction = event.key === 'ArrowUp' ? 1 : -1;
        const before = event.currentTarget.value;
        const input = internalRef.current;
        requestAnimationFrame(() => {
            if (!input) return;
            const after = input.value;
            const upgraded = stepTimeSegment(before, after, step, direction);
            if (upgraded === null) {
                // Hour-segment move (or nothing usable changed): keep the native
                // result, but sync React state so the controlled value matches.
                if (after !== value) {
                    setValue(after);
                    onChange(after);
                }
                return;
            }
            setValue(upgraded);
            onChange(upgraded);
            input.value = upgraded;
        });
    };
    const LabelComponent = slim ? SlimLabel : Label;
    const hasLabel = Boolean(label);
    const inputId = id || name;
    const messageId = `${inputId}-message`;
    const hasError = invalid || (!!message && informationType === InformationType.ERROR);
    // The caller's own descriptions (help text elsewhere on the page) and the message, if any.
    const describedBy =
        [nativeProps['aria-describedby'], message ? messageId : undefined]
            .filter(Boolean)
            .join(' ') || undefined;
    const inputField = (
        <Wrapper $flex={flex}>
            <Container
                $disabled={disabled}
                $slim={slim}
                $size={size}
                $readOnly={readOnly}
                $hasMessage={!!message}
                $invalid={invalid}
                $informationType={informationType}
                $hasLabel={hasLabel}
                onClick={(event: MouseEvent<HTMLDivElement>) => {
                    // Clicking the field padding/label focuses the input. But if
                    // the click already landed on the input, let the browser place
                    // the caret natively — calling focus() here would reset a
                    // <input type="time"> to its first (hour) segment, ignoring
                    // which segment the user actually clicked.
                    if (event.target !== internalRef.current) {
                        internalRef.current?.focus();
                    }
                }}
            >
                {icon && (
                    <IconWrapper $size={size}>
                        <Icon icon={icon} size={ICON_SIZE} />
                    </IconWrapper>
                )}
                <Content $size={size}>
                    {mode === TextInputMode.CURRENCY &&
                        currencyPosition === CurrencyPosition.PREFIX && (
                            <CurrencySymbol visible={isFocused || value.length > 0}>
                                {currencySymbol}
                            </CurrencySymbol>
                        )}
                    <Input
                        {...nativeProps}
                        disabled={disabled}
                        id={inputId}
                        name={name}
                        value={value}
                        type={modeToType[mode]}
                        onChange={onTextChange}
                        onFocus={onTextFocus}
                        onBlur={onTextBlur}
                        onKeyDown={onTimeKeyDown}
                        data-test-id={name}
                        ref={internalRef}
                        autoComplete={autoComplete ?? autocomplete}
                        readOnly={readOnly}
                        $hasMessage={!!message}
                        $invalid={invalid}
                        $informationType={informationType}
                        $hasIcon={!!icon}
                        $size={size}
                        $hasLabel={hasLabel}
                        placeholder={placeholder}
                        max={max}
                        min={min}
                        step={step}
                        aria-invalid={hasError}
                        aria-describedby={describedBy}
                        size={1}
                    />
                    {mode === TextInputMode.CURRENCY &&
                        currencyPosition === CurrencyPosition.SUFFIX && (
                            <CurrencySymbol visible={isFocused || value.length > 0}>
                                {currencySymbol}
                                {unit && ` / ${unit}`}
                            </CurrencySymbol>
                        )}
                    {readOnly && (
                        <ReadOnlyIcon $size={size}>
                            <Icon
                                icon={LockCircleIcon}
                                color={theme.inputReadOnlyIconColor}
                                size={ICON_SIZE}
                            />
                        </ReadOnlyIcon>
                    )}
                    {unit &&
                        (mode !== TextInputMode.CURRENCY ||
                            currencyPosition === CurrencyPosition.PREFIX) && (
                            <Unit visible={isFocused || value.length > 0}>{`/ ${unit}`}</Unit>
                        )}
                </Content>
                <LabelComponent
                    $hasIcon={!!icon}
                    $informationType={informationType}
                    $hasMessage={!!message}
                    $invalid={invalid}
                    $disabled={disabled}
                    $size={size}
                    htmlFor={inputId}
                    data-always-float={mode === TextInputMode.TIME ? 'true' : undefined}
                >
                    {label}
                </LabelComponent>
            </Container>

            <InputMessage id={messageId} message={message} informationType={informationType} />
        </Wrapper>
    );
    if (helpText) {
        const tooltipText = typeof helpText === 'string' ? helpText : undefined;
        return (
            <Composite justify="space-between" align="center" noWrap gap={16} fillParent>
                <HelpIcon
                    type="button"
                    aria-label={tooltipText ?? 'Help'}
                    onClick={(e: MouseEvent<HTMLButtonElement>) => e.preventDefault()} // keep focus
                >
                    <WithTooltip text={tooltipText}>
                        <Icon icon={InformationIcon} size={24} />
                    </WithTooltip>
                </HelpIcon>
                {inputField}
            </Composite>
        );
    }
    return inputField;
}
